import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Alert, Autocomplete, Badge, Box, Button, Card, CardActionArea,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Fab,
  FormControlLabel, IconButton, InputAdornment, MenuItem, Paper, Snackbar,
  Switch, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Autorenew as AutorenewIcon,
  ClearAll as ClearAllIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  PersonAdd as PersonAddIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  TaskAlt as TaskAltIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import axios from 'axios';
import debounce from 'lodash.debounce';
import ClienteRapidoDialog from '../components/ClienteRapidoDialog';
import { obtenerEmpresaConfig } from '../utils/empresaConfig';

const TIPOS = [
  { key: 'NV',      label: 'N.Venta',  labelLargo: 'Nota de Venta', codigo: 'NV', serieFiltro: 'NV' },
  { key: 'BOLETA',  label: 'Boleta',   labelLargo: 'Boleta',         codigo: '03', serieFiltro: '03' },
  { key: 'FACTURA', label: 'Factura',  labelLargo: 'Factura',        codigo: '01', serieFiltro: '01' },
];

const FORMATOS = [
  { key: 'T80', label: 'T80' },
  { key: 'A5',  label: 'A5'  },
  { key: 'A4',  label: 'A4'  },
];

// Mismas claves de localStorage que usan Factura/Boleta/Nota de Venta al marcar
// "Guardar <formato> como predeterminado" en su diálogo de impresión — así Venta
// Rápida respeta el mismo formato que la empresa ya eligió para cada documento.
const FORMATO_KEY_POR_TIPO = {
  NV:      'nota_venta_pdf_formato_default',
  BOLETA:  'boleta_pdf_formato_default',
  FACTURA: 'factura_pdf_formato_default',
};

const CARD_H = 118; // px — altura fija de tarjeta de producto (necesaria para flip 3D)

const extraerSucursales = (alms) => {
  const map = new Map();
  (alms || []).forEach(a => {
    if (a.sucursal_id && !map.has(a.sucursal_id))
      map.set(a.sucursal_id, { id: a.sucursal_id, nombre: a.sucursal_nombre || `Sucursal ${a.sucursal_id}` });
  });
  return [...map.values()];
};

const VentaRapida = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Tipo doc y formato
  const [tipoDoc, setTipoDoc] = useState('NV');
  const [formato, setFormato] = useState('T80');

  // Productos
  const [busqueda,         setBusqueda]         = useState('');
  const [productos,        setProductos]        = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [flippedCards,     setFlippedCards]     = useState({});

  // Carrito
  const [carrito,    setCarrito]    = useState([]);
  const [mobileView, setMobileView] = useState('productos');

  // Cliente (unificado para todos los tipos de comprobante)
  const [cliente,         setCliente]         = useState(null);
  const [clienteInput,    setClienteInput]    = useState('');
  const [clientes,        setClientes]        = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);

  // ClienteRapidoDialog
  const [clienteRapidoOpen, setClienteRapidoOpen] = useState(false);

  // Series y almacenes
  const [serie,     setSerie]     = useState('');
  const [series,    setSeries]    = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [almacen,   setAlmacen]   = useState(null);
  const [sucursal,  setSucursal]  = useState(null);

  // Config y UI
  const [observaciones, setObservaciones] = useState('');
  const [preciosConIgv, setPreciosConIgv] = useState(false);
  const [guardando,     setGuardando]     = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [snackbar,      setSnackbar]      = useState({ open: false, msg: '', severity: 'success', pdfId: null, pdfTipo: null, formato: 'T80' });

  // Scanner
  const [scannerCode, setScannerCode] = useState('');
  const scannerRef   = useRef(null);
  const almacenRef        = useRef(null);
  almacenRef.current      = almacen;
  const busquedaRef       = useRef('');
  busquedaRef.current     = busqueda;
  const almacenFirstRun   = useRef(true);

  // Derivados
  const tipoInfo           = TIPOS.find(t => t.key === tipoDoc) || TIPOS[0];
  const sucursales         = extraerSucursales(almacenes);
  const almacenesFiltrados = sucursal ? almacenes.filter(a => a.sucursal_id === sucursal.id) : almacenes;

  // ─── Flip cards ──────────────────────────────────────────────────
  const toggleFlip = useCallback((id, e) => {
    e.stopPropagation();
    setFlippedCards(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // ─── Cálculo ─────────────────────────────────────────────────────
  const calcularItem = useCallback((item, conIgv) => {
    const usar   = conIgv !== undefined ? conIgv : preciosConIgv;
    const qty    = parseFloat(item.cantidad)        || 0;
    const precio = parseFloat(item.precio_unitario) || 0;
    const tasa   = 0.18;
    let subtotal, igv, total;
    if (usar) {
      total    = Math.round(precio * qty * 100) / 100;
      subtotal = Math.round(total / (1 + tasa) * 100) / 100;
      igv      = Math.round((total - subtotal) * 100) / 100;
    } else {
      subtotal = Math.round(precio * qty * 100) / 100;
      igv      = Math.round(subtotal * tasa * 100) / 100;
      total    = Math.round((subtotal + igv) * 100) / 100;
    }
    return { ...item, subtotal, igv_monto: igv, total };
  }, [preciosConIgv]);

  const resumen = () => {
    const s = carrito.reduce((a, i) => a + (i.subtotal  || 0), 0);
    const g = carrito.reduce((a, i) => a + (i.igv_monto || 0), 0);
    const t = carrito.reduce((a, i) => a + (i.total     || 0), 0);
    return { subtotal: Math.round(s*100)/100, igv: Math.round(g*100)/100, total: Math.round(t*100)/100 };
  };

  // ─── Init ────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      const [almRes, cfgRes] = await Promise.allSettled([
        axios.get('/api/almacen/mis-almacenes').then(r => r.data || []),
        obtenerEmpresaConfig(),
      ]);
      const alms = almRes.status === 'fulfilled' ? almRes.value : [];
      const cfg  = cfgRes.status === 'fulfilled' ? cfgRes.value : {};

      setAlmacenes(alms);
      if (cfg?.precios_incluyen_igv != null) setPreciosConIgv(Boolean(cfg.precios_incluyen_igv));

      const sucs = extraerSucursales(alms);
      if (sucs.length === 1) setSucursal(sucs[0]);
      if (alms.length >= 1) setAlmacen(alms[0]); // siempre auto-selecciona el primero

      fetchProductos('', alms.length >= 1 ? alms[0].id : undefined);
    };
    init();
  }, []);

  useEffect(() => { fetchSeries(tipoInfo.serieFiltro); }, [tipoDoc]);

  // Autoseleccionar el formato de impresión según lo que esté guardado como
  // predeterminado para ese tipo de documento (lo que el usuario marcó en
  // Factura/Boleta/Nota de Venta). Si no hay nada guardado, cae a T80.
  useEffect(() => {
    try {
      const key = FORMATO_KEY_POR_TIPO[tipoDoc];
      const guardado = key ? localStorage.getItem(key) : null;
      setFormato(FORMATOS.some(f => f.key === guardado) ? guardado : 'T80');
    } catch {
      setFormato('T80');
    }
  }, [tipoDoc]);

  useEffect(() => {
    const handler = e => { if (e.key === 'F8') { e.preventDefault(); scannerRef.current?.focus(); } };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (!sucursal) return;
    const filtered = almacenes.filter(a => a.sucursal_id === sucursal.id);
    if (filtered.length === 1) {
      setAlmacen(filtered[0]);
    } else if (almacen && !filtered.some(a => a.id === almacen.id)) {
      setAlmacen(null);
    }
  }, [sucursal]); // eslint-disable-line

  // Refetch productos cada vez que cambia el almacén (por dropdown o por cascada de sucursal)
  useEffect(() => {
    if (almacenFirstRun.current) { almacenFirstRun.current = false; return; }
    fetchProductos(busquedaRef.current, almacen?.id);
  }, [almacen?.id]); // eslint-disable-line

  // ─── Fetch ───────────────────────────────────────────────────────
  const fetchProductos = async (q, almacenId) => {
    setLoadingProductos(true);
    try {
      const params = { q: q || '' };
      if (almacenId) params.almacen_id = almacenId;
      const res = await axios.get('/api/productos/buscar-popup', { params });
      setProductos(res.data || []);
    } catch { setProductos([]); }
    setLoadingProductos(false);
  };

  const debouncedFetch = useCallback(
    debounce((q) => fetchProductos(q, almacenRef.current?.id), 300),
    []
  );

  const fetchSeries = async (filtro) => {
    try {
      const res      = await axios.get('/api/series');
      const filtered = (res.data || []).filter(s => s.tipo_documento === filtro);
      setSeries(filtered);
      setSerie(filtered.length > 0 ? filtered[0].serie : '');
    } catch {}
  };

  // Carga todos los clientes una vez (el servidor ignora q, filtro es client-side)
  const fetchClientes = useCallback(debounce(async () => {
    setLoadingClientes(true);
    try {
      const res = await axios.get('/api/auxiliares', { params: { tipo: 'CLIENTE' } });
      setClientes(res.data || []);
    } catch { setClientes([]); }
    setLoadingClientes(false);
  }, 150), []);


  // ─── Carrito ─────────────────────────────────────────────────────
  const agregarAlCarrito = useCallback((prod) => {
    setCarrito(prev => {
      const currentAlmacenId = almacenRef.current?.id || '';
      const idx = prev.findIndex(i => i.producto_id === prod.id && i.almacen_id === currentAlmacenId);
      if (idx >= 0)
        return prev.map((i, j) => j !== idx ? i : calcularItem({ ...i, cantidad: (parseFloat(i.cantidad) || 0) + 1 }));
      return [...prev, calcularItem({
        producto_id:          prod.id,
        codigo_producto:      prod.codigo || '',
        descripcion_producto: prod.nombre || '',
        cantidad:             1,
        precio_unitario:      prod.precio || 0,
        unidad_medida_id:     prod.unidad_medida_id || null,
        unidad_medida:        prod.unidad_medida || 'NIU',
        descuento_monto:      0,
        descuento_porcentaje: 0,
        tipo_igv:             '10',
        igv_monto:            0,
        subtotal:             0,
        total:                0,
        almacen_id:           almacenRef.current?.id || '',
        observaciones:        '',
        stock:                prod.afecta_kardex == 1 ? Number(prod.stock_total || 0) : null,
      })];
    });
  }, [calcularItem]);

  const handleScannerEnter = async (code) => {
    const trimmed = (code || '').trim();
    if (!trimmed) return;
    let prod = productos.find(p => p.codigo === trimmed);
    if (!prod) {
      try {
        const res = await axios.get('/api/productos/buscar-popup', { params: { q: trimmed } });
        prod = (res.data || []).find(p => p.codigo === trimmed);
      } catch {}
    }
    if (!prod) {
      setSnackbar({ open: true, msg: `Código "${trimmed}" no encontrado`, severity: 'warning', pdfId: null, formato });
    } else {
      agregarAlCarrito(prod);
      setSnackbar({ open: true, msg: `✓ ${prod.nombre} agregado`, severity: 'success', pdfId: null, formato });
    }
    setScannerCode('');
  };

  const cambiarCantidad = (idx, delta) =>
    setCarrito(prev => prev.map((i, j) => j !== idx ? i
      : calcularItem({ ...i, cantidad: Math.max(1, (parseFloat(i.cantidad) || 1) + delta) })));

  const setCantidadDirecta = (idx, val) => {
    const qty = Math.max(1, parseFloat(val) || 1);
    setCarrito(prev => prev.map((i, j) => j !== idx ? i : calcularItem({ ...i, cantidad: qty })));
  };

  const setPrecioItem = (idx, val) => {
    const precio = parseFloat(val) || 0;
    setCarrito(prev => prev.map((i, j) => j !== idx ? i : calcularItem({ ...i, precio_unitario: precio })));
  };

  const quitarItem     = (idx) => setCarrito(prev => prev.filter((_, j) => j !== idx));
  const limpiarCarrito = ()    => setCarrito([]);

  // ─── Cliente rápido ──────────────────────────────────────────────
  const handleClienteCreado = (nuevoCliente) => {
    setCliente(nuevoCliente);
    setClienteInput((nuevoCliente.razon_social || `${nuevoCliente.nombres || ''} ${nuevoCliente.apellidos || ''}`).trim());
    setClientes(prev => prev.some(c => c.id === nuevoCliente.id) ? prev : [nuevoCliente, ...prev]);
    setClienteRapidoOpen(false);
  };

  // ─── Confirmar venta ─────────────────────────────────────────────
  const confirmarVenta = async () => {
    const clienteId = cliente?.id || null;
    if (!clienteId) {
      setSnackbar({ open: true, msg: 'Debes seleccionar un cliente antes de guardar.', severity: 'error', pdfId: null, formato });
      return;
    }
    const itemsSinStock = carrito.filter(i => i.stock != null && i.cantidad > i.stock);
    if (itemsSinStock.length > 0) {
      const nombres = itemsSinStock.map(i => i.descripcion_producto || `Producto ${i.producto_id}`).join(', ');
      setSnackbar({ open: true, msg: `Sin stock suficiente: ${nombres}`, severity: 'error', pdfId: null, formato });
      return;
    }
    setGuardando(true);
    try {
      const { subtotal, igv, total } = resumen();
      const usuario  = (() => { try { return JSON.parse(localStorage.getItem('usuario') || 'null'); } catch { return null; } })();
      const detalles = carrito.map(({ stock, ...i }) => ({ ...i, almacen_id: i.almacen_id || almacen?.id || '' }));

      const base = {
        serie,
        numero:                      '',
        fecha_emision:               new Date().toLocaleDateString('en-CA'),
        fecha_validez:               '',
        cliente_id:                  clienteId,
        vendedor_id:                 usuario?.id || null,
        moneda:                      'PEN',
        tipo_cambio:                 1,
        condicion_pago:              'CONTADO',
        cuotas_json:                 '',
        subtotal,
        descuento_global:            0,
        porcentaje_descuento_global: 0,
        total_igv:                   igv,
        total,
        total_gravada:               subtotal,
        total_exonerada:             0,
        total_inafecta:              0,
        total_gratuita:              0,
        total_exportacion:           0,
        total_otros_cargos:          0,
        total_icbper:                0,
        total_anticipos:             0,
        total_percepcion:            0,
        total_otros_impuestos:       0,
        observaciones,
        estado:                      'PENDIENTE',
        sucursal_id:                 sucursal?.id || almacen?.sucursal_id || '',
        adjuntos:                    [],
        adjuntos_json:               '[]',
        es_anticipo:                 false,
        anticipo_referencia_id:      null,
        detraccion_aplicada:         false,
        detraccion_monto:            0,
        detraccion_cuenta:           '',
        retencion_aplicada:          false,
        retencion_monto:             0,
        usuario_creacion:            usuario?.username || '',
        usuario_modificacion:        usuario?.username || '',
        detalles,
      };

      let doc;
      if (tipoDoc === 'NV') {
        const res = await axios.post('/api/nota-venta', base);
        doc = res.data;
      } else {
        const res = await axios.post('/api/comprobante', { ...base, tipo_comprobante: tipoInfo.codigo });
        doc = res.data;
      }

      setCarrito([]);
      setObservaciones('');
      setCliente(null); setClienteInput('');
      setConfirmDialog(false);

      const pdfTipo = tipoDoc === 'NV' ? 'nota-venta' : 'comprobante';
      setSnackbar({
        open:     true,
        msg:      `✓ ${tipoInfo.labelLargo} ${doc?.serie || ''}-${String(doc?.numero || '').padStart(8, '0')} registrada`,
        severity: 'success',
        pdfId:    doc?.id || null,
        pdfTipo,
        formato,
      });
    } catch (e) {
      setSnackbar({ open: true, msg: `Error: ${e.response?.data?.error || e.message}`, severity: 'error', pdfId: null, formato });
    }
    setGuardando(false);
  };

  const { subtotal, igv, total } = resumen();
  const clienteActual = cliente;
  const getNombreAlmacen = (almacenId) => {
    if (!almacenId) return null;
    return almacenes.find(a => a.id === almacenId)?.nombre || null;
  };

  // ─── Render ──────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1, md: 2 }, display: 'flex', gap: 1.5, height: '80vh' }}>

      {/* ════ COLUMNA IZQUIERDA: cabecera + búsqueda + productos ════ */}
      <Box sx={{
        flex: 1,
        display: { xs: mobileView === 'productos' ? 'flex' : 'none', sm: 'flex' },
        flexDirection: 'column',
        gap: 1,
        minHeight: 0,
        minWidth: 0,
      }}>

        {/* ── Fila 1: Título + Tipo doc + Formato ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PointOfSaleIcon color="primary" />
            <Typography variant="h6" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>Venta Rápida</Typography>
          </Box>

          {/* Tipo documento */}
          <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {TIPOS.map(t => (
              <Button key={t.key} size="small"
                variant={tipoDoc === t.key ? 'contained' : 'text'}
                color={tipoDoc === t.key ? 'primary' : 'inherit'}
                onClick={() => { setTipoDoc(t.key); setClienteDoc(null); setDocInput(''); setDocError(''); }}
                sx={{ borderRadius: 0, px: 1.5, minWidth: 0, textTransform: 'none', fontSize: '0.78rem' }}
              >
                {t.label}
              </Button>
            ))}
          </Box>

          {/* Formato impresión */}
          <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {FORMATOS.map(f => (
              <Button key={f.key} size="small"
                variant={formato === f.key ? 'contained' : 'text'}
                color={formato === f.key ? 'secondary' : 'inherit'}
                onClick={() => setFormato(f.key)}
                sx={{ borderRadius: 0, px: 1.25, minWidth: 0, textTransform: 'none', fontSize: '0.75rem' }}
              >
                {f.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* ── Fila 2: Sucursal + Almacén + Serie + Cliente ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>

          {sucursales.length > 1 && (
            <TextField select size="small" label="Sucursal" value={sucursal?.id || ''}
              onChange={e => setSucursal(sucursales.find(s => s.id === e.target.value) || null)}
              sx={{ minWidth: 170 }}
            >
              {sucursales.map(s => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
            </TextField>
          )}

          {almacenesFiltrados.length > 1 && (
            <TextField select size="small" label="Almacén" value={almacen?.id || ''}
              onChange={e => {
                const a = almacenesFiltrados.find(x => x.id === e.target.value) || null;
                setAlmacen(a);
              }}
              sx={{ minWidth: 170 }}
            >
              {almacenesFiltrados.map(a => <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>)}
            </TextField>
          )}

          {series.length > 0 && (
            <TextField select size="small" label="Serie" value={serie}
              onChange={e => setSerie(e.target.value)} sx={{ minWidth: 110 }}>
              {series.map(s => <MenuItem key={s.serie} value={s.serie}>{s.serie}</MenuItem>)}
            </TextField>
          )}

          <Autocomplete size="small" sx={{ minWidth: 230 }}
            options={clientes}
            getOptionLabel={op => op ? (op.razon_social || `${op.nombres || ''} ${op.apellidos || ''}`).trim() : ''}
            filterOptions={(opts, { inputValue }) => {
              if (!inputValue) return opts;
              const v = inputValue.toLowerCase();
              return opts.filter(o =>
                (o.razon_social || '').toLowerCase().includes(v) ||
                (o.nombres || '').toLowerCase().includes(v) ||
                (o.apellidos || '').toLowerCase().includes(v) ||
                (o.numero_documento || '').includes(v)
              );
            }}
            inputValue={clienteInput}
            onInputChange={(_, v, reason) => {
              if (reason === 'input') setClienteInput(v);
              else if (reason === 'clear') { setClienteInput(''); setCliente(null); }
            }}
            onOpen={() => { if (clientes.length === 0) fetchClientes(); }}
            onChange={(_, v) => { setCliente(v); setClienteInput(v ? (v.razon_social || `${v.nombres || ''} ${v.apellidos || ''}`).trim() : ''); }}
            value={cliente}
            loading={loadingClientes}
            renderInput={params => (
              <TextField {...params} label="Cliente (requerido)" error={!cliente && carrito.length > 0} />
            )}
            noOptionsText={
              clienteInput.trim() ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Sin resultados</Typography>
                  <Button size="small" startIcon={<PersonAddIcon fontSize="small" />}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => setClienteRapidoOpen(true)}>
                    Registrar
                  </Button>
                </Box>
              ) : 'Escribe para buscar...'
            }
          />
        </Box>

        {/* ── Fila 3: Observaciones + Switch IGV ── */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1,
        }}>
          <TextField size="small" label="Observaciones"
            value={observaciones}
            onChange={e => setObservaciones(e.target.value)}
            sx={{ flex: 1, width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 200 } }}
          />
          <FormControlLabel
            control={
              <Switch size="small" checked={preciosConIgv}
                onChange={e => { const v = e.target.checked; setPreciosConIgv(v); setCarrito(prev => prev.map(i => calcularItem(i, v))); }}
              />
            }
            label={<Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Precios incl. IGV</Typography>}
            sx={{ flexShrink: 0, mr: 0, alignSelf: { xs: 'center', sm: 'auto' } }}
          />
        </Box>

        {/* ── Búsqueda + scanner ── */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          <TextField fullWidth size="small" label="Buscar producto"
            value={busqueda}
            onChange={e => { setBusqueda(e.target.value); debouncedFetch(e.target.value); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
          />
          <TextField size="small" label="Código (F8)"
            inputRef={scannerRef}
            value={scannerCode}
            onChange={e => setScannerCode(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleScannerEnter(scannerCode); } }}
            sx={{ minWidth: { xs: '100%', sm: 158 } }}
            inputProps={{ style: { fontFamily: 'monospace' } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><QrCodeScannerIcon fontSize="small" color="primary" /></InputAdornment> }}
          />
        </Box>

        {/* ── Grid de productos con flip ── */}
        <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
          {loadingProductos ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}><CircularProgress size={32} /></Box>
          ) : productos.length === 0 ? (
            <Box sx={{ textAlign: 'center', pt: 6 }}>
              <Typography color="text.secondary">No se encontraron productos</Typography>
            </Box>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(128px, 1fr))',
              gridAutoRows: `${CARD_H}px`,
              gap: 1,
            }}>
              {productos.map(prod => {
                const imgUrl    = prod.imagen_url || prod.imagen || prod.foto || null;
                const isFlipped = !!flippedCards[prod.id];
                return (
                  /* Outer Box es el containing block para el botón flip (position:relative) */
                  <Box key={prod.id} sx={{ perspective: '900px', height: CARD_H, position: 'relative' }}>

                    {/* Contenedor 3D que gira */}
                    <Box sx={{
                      position: 'absolute', inset: 0,
                      transformStyle: 'preserve-3d',
                      transition: 'transform 0.42s ease',
                      transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    }}>
                      {/* ── Cara frontal ── */}
                      <Card variant="outlined" sx={{
                        position: 'absolute', inset: 0,
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        overflow: 'hidden',
                        transition: 'border-color .15s, box-shadow .15s',
                        '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
                      }}>
                        <CardActionArea onClick={() => agregarAlCarrito(prod)}
                          sx={{ p: 1.25, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                          <Typography variant="caption" color="text.disabled" noWrap
                            sx={{ fontSize: '0.67rem', display: 'block', width: '100%', pr: imgUrl ? 2.2 : 0 }}>
                            {prod.codigo || '—'}
                          </Typography>
                          <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.77rem', lineHeight: 1.3, mb: 0.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 32, width: '100%' }}>
                            {prod.nombre}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="primary.main" sx={{ fontSize: '0.84rem' }}>
                            S/ {Number(prod.precio || 0).toFixed(2)}
                          </Typography>
                          {prod.afecta_kardex == 1 && (
                            <Typography variant="caption" sx={{ color: Number(prod.stock_total || 0) > 0 ? 'success.main' : 'error.main', fontSize: '0.67rem' }}>
                              Stock: {prod.stock_total != null ? prod.stock_total : 0}
                            </Typography>
                          )}
                        </CardActionArea>
                      </Card>

                      {/* ── Cara trasera (imagen) ── */}
                      {imgUrl && (
                        <Card variant="outlined" sx={{
                          position: 'absolute', inset: 0,
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          transform: 'rotateY(180deg)',
                          overflow: 'hidden',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: 'background.paper',
                        }}>
                          <Box component="img" src={imgUrl} alt={prod.nombre}
                            sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.75 }}
                          />
                        </Card>
                      )}
                    </Box>

                    {/* Botón flip — FUERA del contenedor 3D, siempre visible sobre ambas caras */}
                    {imgUrl && (
                      <Tooltip title={isFlipped ? 'Volver al producto' : 'Ver imagen'} placement="top">
                        <IconButton size="small"
                          sx={{
                            position: 'absolute', top: 4, right: 4, zIndex: 10,
                            p: '3px',
                            bgcolor: isFlipped ? 'primary.main' : 'rgba(255,255,255,0.88)',
                            color: isFlipped ? 'white' : 'text.secondary',
                            boxShadow: 1,
                            '&:hover': { bgcolor: isFlipped ? 'primary.dark' : 'white', boxShadow: 2 },
                          }}
                          onClick={e => toggleFlip(prod.id, e)}
                        >
                          <AutorenewIcon sx={{
                            fontSize: 13,
                            transition: 'transform 0.42s ease',
                            transform: isFlipped ? 'rotate(180deg)' : 'none',
                          }} />
                        </IconButton>
                      </Tooltip>
                    )}

                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* ════ CARRITO — sidebar derecho, altura completa ════ */}
      <Paper variant="outlined" sx={{
        width: { xs: '100%', sm: 300, md: 320 },
        flexShrink: 0,
        display: { xs: mobileView === 'carrito' ? 'flex' : 'none', sm: 'flex' },
        flexDirection: 'column',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <Box sx={{ px: 1.5, py: 1, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {isMobile && (
              <IconButton size="small" sx={{ color: 'white', p: 0.25 }} onClick={() => setMobileView('productos')}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <ShoppingCartIcon fontSize="small" />
            <Typography fontWeight="bold" variant="body1">
              Carrito {carrito.length > 0 && `(${carrito.length})`}
            </Typography>
          </Box>
          <Tooltip title="Vaciar carrito">
            <IconButton size="small" sx={{ color: 'white' }} onClick={limpiarCarrito} disabled={carrito.length === 0}>
              <ClearAllIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Items */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
          {carrito.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <ShoppingCartIcon sx={{ fontSize: 42, color: 'text.disabled' }} />
              <Typography color="text.secondary" variant="body2" mt={1}>Carrito vacío</Typography>
              <Typography color="text.disabled" variant="caption">Toca un producto para agregar</Typography>
            </Box>
          ) : carrito.map((item, idx) => (
            <Box key={idx} sx={{ mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
                <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.78rem', lineHeight: 1.3, flex: 1 }}>
                  {item.descripcion_producto}
                </Typography>
                <IconButton size="small" onClick={() => quitarItem(idx)} sx={{ p: 0.25, flexShrink: 0 }}>
                  <DeleteIcon sx={{ fontSize: 15 }} color="error" />
                </IconButton>
              </Box>
              {(item.codigo_producto || getNombreAlmacen(item.almacen_id)) && (
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem' }}>
                  {[item.codigo_producto, getNombreAlmacen(item.almacen_id)].filter(Boolean).join(' · ')}
                </Typography>
              )}

              {/* [- qty +] × S/ [precio] = S/ total */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', mt: 0.75 }}>
                <IconButton size="small" onClick={() => cambiarCantidad(idx, -1)}
                  sx={{ p: 0.2, border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }}>
                  <RemoveIcon sx={{ fontSize: 13 }} />
                </IconButton>
                <TextField size="small" value={item.cantidad}
                  onChange={e => setCantidadDirecta(idx, e.target.value)}
                  sx={{ width: 38 }}
                  inputProps={{ style: { textAlign: 'center', padding: '2px', fontSize: '0.8rem' } }}
                />
                <IconButton size="small" onClick={() => cambiarCantidad(idx, 1)}
                  sx={{ p: 0.2, border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }}>
                  <AddIcon sx={{ fontSize: 13 }} />
                </IconButton>

                <Typography variant="caption" color="text.secondary" sx={{ mx: '1px' }}>×</Typography>

                <TextField size="small"
                  value={item.precio_unitario}
                  onChange={e => setPrecioItem(idx, e.target.value)}
                  onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) setPrecioItem(idx, v.toFixed(2)); }}
                  sx={{ width: 74 }}
                  inputProps={{ style: { textAlign: 'right', padding: '2px 4px', fontSize: '0.8rem' } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>S/</Typography></InputAdornment> }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  <Typography variant="body2" fontWeight="bold" color="primary.main"
                    sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                    = S/ {Number(item.total || 0).toFixed(2)}
                  </Typography>
                  {item.stock != null && item.cantidad > item.stock && (
                    <Tooltip title={`Stock disponible: ${item.stock}`}>
                      <WarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Totales + confirmar */}
        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography variant="body2">S/ {subtotal.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">IGV (18%)</Typography>
            <Typography variant="body2">S/ {igv.toFixed(2)}</Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
            <Typography fontWeight="bold">TOTAL</Typography>
            <Typography fontWeight="bold" variant="h6" color="primary.main">S/ {total.toFixed(2)}</Typography>
          </Box>
          <Button fullWidth variant="contained" size="large" color="primary"
            startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : <TaskAltIcon />}
            onClick={() => setConfirmDialog(true)}
            disabled={carrito.length === 0 || guardando || !serie}
          >
            {guardando ? 'Procesando...' : `CONFIRMAR ${tipoInfo.label.toUpperCase()}`}
          </Button>
        </Box>
      </Paper>

      {/* ── FAB móvil ── */}
      {isMobile && mobileView === 'productos' && (
        <Fab color="primary"
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200 }}
          onClick={() => setMobileView('carrito')}
        >
          <Badge badgeContent={carrito.length} color="error" max={99}>
            <ShoppingCartIcon />
          </Badge>
        </Fab>
      )}

      {/* ── Diálogo confirmar ── */}
      <Dialog open={confirmDialog} onClose={() => !guardando && setConfirmDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar {tipoInfo.labelLargo}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>Tipo: <strong>{tipoInfo.labelLargo}</strong></Typography>
          <Typography variant="body2" gutterBottom>Serie: <strong>{serie}</strong></Typography>
          {clienteActual && (
            <Typography variant="body2" gutterBottom>
              Cliente: <strong>{clienteActual.razon_social || clienteActual.nombre}</strong>
            </Typography>
          )}
          <Typography variant="body2" gutterBottom>{carrito.length} producto(s)</Typography>
          {observaciones && (
            <Typography variant="body2" gutterBottom>Obs: <em>{observaciones}</em></Typography>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography fontWeight="bold">TOTAL A COBRAR</Typography>
            <Typography fontWeight="bold" color="primary.main" variant="h6">S/ {total.toFixed(2)}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)} disabled={guardando}>Cancelar</Button>
          <Button variant="contained" onClick={confirmarVenta} disabled={guardando}
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <TaskAltIcon />}>
            {guardando ? 'Registrando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.pdfId ? 8000 : 3000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          action={snackbar.pdfId ? (
            <Button color="inherit" size="small" startIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => { const tk = localStorage.getItem('token') || ''; window.open(`/api/reporte-pdf/${snackbar.pdfTipo}/${snackbar.pdfId}?formato=${snackbar.formato || 'T80'}&mode=inline&_ts=${Date.now()}&token=${tk}`, '_blank'); }}>
              Ver PDF
            </Button>
          ) : undefined}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>

      {/* ── Registrar cliente rápido ── */}
      <ClienteRapidoDialog
        open={clienteRapidoOpen}
        onClose={() => setClienteRapidoOpen(false)}
        onClienteCreado={handleClienteCreado}
        initialQuery={clienteInput}
      />
    </Box>
  );
};

export default VentaRapida;
